// ─── Carte de validation réutilisable ────────────────────────────────────────

import { toast } from "sonner";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { getFileValidationByEntity } from "../../api/fileAttachement";

type ValidationLevelCardProps = {
  level: string; // "1" | "2" | ...
  validation: any; // l'objet validationLevel retourné par l'API
  validatorParam: any; // decisionParameter?.validator1 ou validator2
  currentUserId: string | number; // userData?.id
  hasLoaded: boolean;
  deleteValidation: ((validation: any) => void) | undefined; // fonction de suppression, ou undefined si non autorisé
  onValidate: (level: string) => void;
  onSaved: () => void;
};

// === Voir / Télécharger un support ===
const handleGetSupportFile = async (idEntity: string) => {
  try {
    const validationFile = await getFileValidationByEntity(
      "DecisionValidation",
      idEntity,
      buildPermissionContext(Resource.MEMOIRE, Action.READ),
    );
    // console.log("validationFile: ", validationFile);
    window.open(
      validationFile.data[0].temporaryUrl,
      "_blank",
      "noopener,noreferrer",
    );
  } catch (error: any) {
    // console.log("Erreur lors de l'ouverture du fichier: ", error);
    toast.error(
      error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
    );
  }
};

const ValidationLevelCard = ({
  level,
  validation,
  validatorParam,
  currentUserId,
  hasLoaded,
  onValidate,
  deleteValidation,
  onSaved,
}: ValidationLevelCardProps) => {
  if (!hasLoaded) return null;

  // ── Config visuelle selon le statut ──
  const config = (() => {
    if (validation?.statut === "Validé")
      return {
        border: "#16a34a",
        bg: "#dcfce7",
        color: "#16a34a",
        icon: "ti-check",
        label: `Validé le ${new Date(validation.createdAt).toLocaleDateString("fr-FR")}`,
        user: `${validation?.UserProfil?.firstname} ${validation?.UserProfil?.lastname}`,
        actions: (
          <>
            <button
              className="btn btn-light btn-md d-inline-flex align-items-center"
              title="Voir la pièce jointe"
              onClick={(e) => {
                e.preventDefault();
                handleGetSupportFile(validation?.id);
              }}
            >
              <i className="ti ti-paperclip" />
            </button>
            {validatorParam?.id == currentUserId && (
              <button
                className="btn btn-danger ms-2 btn-md d-inline-flex align-items-center"
                title="Supprimer"
                onClick={
                  deleteValidation
                    ? () => deleteValidation(validation)
                    : undefined
                }
              >
                <i className="ti ti-trash" />
              </button>
            )}
          </>
        ),
      };

    if (validation?.statut === "Rejeté")
      return {
        border: "#be1f1f",
        bg: "#f9a7a7",
        color: "#be1f1f",
        icon: "ti-x",
        label: `Rejeté le ${new Date(validation.createdAt).toLocaleDateString("fr-FR")}`,
        user: `${validation?.UserProfil?.firstname} ${validation?.UserProfil?.lastname}`,
        actions: (
          <>
            <button
              className="btn btn-light btn-md d-inline-flex align-items-center"
              title="Voir la pièce jointe"
            >
              <i className="ti ti-paperclip" />
            </button>
            {validatorParam?.id == currentUserId && (
              <button
                className="btn btn-danger ms-2 btn-md d-inline-flex align-items-center"
                title="Supprimer"
                onClick={
                  deleteValidation
                    ? () => deleteValidation(validation)
                    : undefined
                }
              >
                <i className="ti ti-trash" />
              </button>
            )}
          </>
        ),
      };

    // Défaut : En attente
    return {
      border: "#ffbc05",
      bg: "#f4ee97",
      color: "#ffbc05",
      icon: "ti-clock-hour-4",
      label: "En attente de validation",
      user: `${validatorParam?.firstname} ${validatorParam?.lastname}`,
      actions:
        validatorParam?.id == currentUserId ? (
          <button
            className="btn btn-warning btn-md d-inline-flex align-items-center"
            onClick={() => onValidate(level)}
          >
            <i className="ti ti-check me-1" /> Valider
          </button>
        ) : null,
    };
  })();

  return (
    <div className="col-12 col-md-6 mb-0">
      <div className="card shadow-sm">
        <div className="card-body d-flex align-items-center justify-content-between p-3">
          <div className="d-flex align-items-center">
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
              style={{
                width: 56,
                height: 56,
                border: `3px solid ${config.border}`,
                background: config.bg,
                color: config.color,
                flexShrink: 0,
              }}
            >
              <i className={`ti ${config.icon} fs-2`} />
            </div>
            <div>
              <h5 className="mb-1">Validation de niveau {level}</h5>
              <p className="d-flex align-items-center flex-wrap mb-0 text-muted">
                <i className="ti ti-calendar-bolt me-1 mb-1" /> {config.label}
                <i className="ti ti-user ms-2 me-1 mb-1" /> Par {config.user}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center align-self-center ms-3">
            {config.actions}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidationLevelCard;
